/* eslint-disable consistent-return,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import 'moment/locale/es';
import moment from 'moment';
// @material-ui/icons
import Check from '@material-ui/icons/Check';
import ArrowUp from '@material-ui/icons/ArrowUpward';
import ArrowDown from '@material-ui/icons/ArrowDownward';
// @material-ui/core components
import FilterStyles from 'assets/jss/AdsFilter';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// global variables
import { flags } from 'config';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
import Card from 'components/UI/Card/Card';
import CardBody from 'components/UI/Card/CardBody';
// our components
import SectionDivider from 'components/common/SectionDivider/SectionDivider';
import Datepicker from 'components/common/DatePicker';

/**
 * Variable to define default selected flagged types
 */
const defaultSelectedFlags = [
  flags.classified.value,
  flags.offensive.value,
  flags.scam.value,
  flags.spam.value,
  flags.joke.value,
];

/**
 * @class
 * @extends React.Component
 */
class FlaggedAdFilter extends React.Component {
  constructor(props) {
    super(props);
    const { appliedFilter } = props;
    this.state = {
      selectedFlags:
        (appliedFilter && appliedFilter.flags) || defaultSelectedFlags,
      localFilter: appliedFilter || null,
      expandFiltersAnimation: false,
    };
  }

  /**
   * @description
   * Change filters.
   *
   * @param {Object} changedFilter - Changed filters values.
   * @returns {*} - No returns value.
   */
  handleChangeFilter = changedFilter => {
    const { localFilter } = this.state;
    const { filter } = this.props;
    const filters = { ...localFilter, ...changedFilter };
    this.setState({ localFilter: filters });
    filter(filters);
  };

  /**
   * @description
   * Update new selection filter type.
   *
   * @param {*} callback - Callback waiting for setState.
   * @returns {*} - An update function.
   */
  updateSelectedCheckboxes = callback => {
    const { selectedFlags } = this.state;
    this.handleChangeFilter(
      selectedFlags.length === 0 ||
        selectedFlags.length === defaultSelectedFlags.length
        ? { flags: undefined }
        : { flags: selectedFlags },
      callback,
    );
  };

  /**
   * @description
   * Handle select status checkboxes.
   *
   * @param {string} checkLabel - Status selected.
   * @param {*} callback - Callback waiting for setState.
   * @returns {undefined} No return.
   */
  handleFlagsCheck = (checkLabel, callback = null) => {
    const { selectedFlags } = this.state;
    if (selectedFlags.indexOf(checkLabel) !== -1) {
      const newSelected = selectedFlags.filter(flag => flag !== checkLabel);
      this.setState({ selectedFlags: newSelected }, () =>
        this.updateSelectedCheckboxes(callback),
      );
    } else {
      this.setState(
        {
          selectedFlags: [...selectedFlags, checkLabel],
        },
        () => this.updateSelectedCheckboxes(callback),
      );
    }
  };

  /**
   * @description
   * Function to expand animation.
   * @returns {*} - Null.
   */
  expandAnimation = () => {
    const { expandFiltersAnimation } = this.state;
    const { showFilters } = this.props;

    this.setState({ expandFiltersAnimation: !expandFiltersAnimation }, () =>
      setTimeout(showFilters, 150),
    );
  };

  renderCollapsedFilters() {
    const { expandFiltersAnimation } = this.state;
    const { classes, t } = this.props;
    return (
      <div
        className={`${classes.animationFilter}
      ${
        expandFiltersAnimation
          ? classes.expandCollapsedFilters
          : classes.collapseCollapsedFilters
      }`}
      >
        <GridContainer>
          <GridItem xs={12} style={{ textAlign: 'center' }}>
            <Button
              color="primary"
              simple
              className={classes.showFilters}
              onClick={this.expandAnimation}
              id="showFilters"
              key="showFilters"
              fullWidth
            >
              <ArrowDown />
              {t('ad.filters.expand')}
            </Button>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  renderFilters() {
    const { expandFiltersAnimation, selectedFlags, localFilter } = this.state;
    const { classes, t, restartFilters } = this.props;
    return (
      <div
        className={`${classes.animationFilter}
      ${
        expandFiltersAnimation ? classes.expandFilters : classes.collapseFilters
      }`}
      >
        <GridContainer>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('flagged_ad.filters.section.ad')}>
              <GridContainer style={{ alignItems: 'flex-end' }}>
                <GridItem xs={12} md={6}>
                  <CustomInput
                    data-cy="flaggedAdCount"
                    id="flagsCountGte"
                    formControlProps={{
                      className: `${classes.top} ${classes.search}`,
                    }}
                    inputProps={{
                      placeholder: t('flagged_ad.filters.count_placeholder'),
                      onChange: e => {
                        const { value } = e.target;
                        return value !== ''
                          ? this.handleChangeFilter({
                              flagsCountGte: value,
                            })
                          : this.handleChangeFilter({
                              flagsCountGte: undefined,
                            });
                      },
                      value:
                        localFilter.flagsCountGte !== undefined
                          ? localFilter.flagsCountGte
                          : '',
                      type: 'text',
                    }}
                  />
                </GridItem>
                <GridItem xs={12} md={6}>
                  <span>{t('flagged_ad.filters.reviewed.filter_title')}</span>
                  <div className={classes.buttonGroup}>
                    <Button
                      data-cy="flaggedAdsReviewedNo"
                      color="info"
                      round
                      className={classes.firstButton}
                      simple={
                        localFilter.reviewed === undefined ||
                        localFilter.reviewed === true
                      }
                      onClick={() =>
                        this.handleChangeFilter({
                          reviewed: false,
                        })
                      }
                      id="review-no"
                      key="review-no"
                    >
                      {t('flagged_ad.filters.reviewed.no')}
                    </Button>
                    <Button
                      data-cy="flaggedAdsReviewedIgnore"
                      color="info"
                      round
                      className={classes.middleButton}
                      simple={localFilter.reviewed !== undefined}
                      onClick={() =>
                        this.handleChangeFilter({
                          reviewed: undefined,
                        })
                      }
                      id="review-ignore"
                      key="review-ignore"
                    >
                      {t('flagged_ad.filters.reviewed.ignore')}
                    </Button>
                    <Button
                      data-cy="flaggedAdsReviewedYes"
                      color="info"
                      round
                      className={classes.lastButton}
                      simple={
                        localFilter.reviewed === undefined ||
                        localFilter.reviewed === false
                      }
                      onClick={() =>
                        this.handleChangeFilter({
                          reviewed: true,
                        })
                      }
                      id="review-yes"
                      key="review-yes"
                    >
                      {t('flagged_ad.filters.reviewed.yes')}
                    </Button>
                  </div>
                </GridItem>
              </GridContainer>
            </SectionDivider>
            <SectionDivider
              label={t('flagged_ad.filters.section.date')}
              style={{ padding: '17px 8px 14px' }}
            >
              <GridItem>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <Datepicker
                      data-cy="flaggedAdFlaggedOnDateFrom"
                      title={t('flagged_ad.filters.date.from')}
                      defaultValue={t(
                        'flagged_ad.filters.date.from_placeholder',
                      )}
                      onChange={date => {
                        if (
                          localFilter.flaggedOnLte &&
                          localFilter.flaggedOnLte < date
                        ) {
                          return;
                        }
                        this.handleChangeFilter({
                          flaggedOnGte: date
                            ? moment(date)
                                .startOf('day')
                                .format()
                            : null,
                        });
                      }}
                      value={
                        localFilter.flaggedOnGte !== undefined &&
                        localFilter.flaggedOnGte !== null
                          ? moment(localFilter.flaggedOnGte)
                          : ''
                      }
                      toToday
                      validGte={localFilter.flaggedOnLte}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <Datepicker
                      data-cy="flaggedAdFlaggedOnDateTo"
                      title={t('flagged_ad.filters.date.to')}
                      defaultValue={t('flagged_ad.filters.date.to_placeholder')}
                      onChange={date => {
                        if (
                          localFilter.flaggedOnGte &&
                          localFilter.flaggedOnGte > date
                        ) {
                          return;
                        }
                        this.handleChangeFilter({
                          flaggedOnLte: date
                            ? moment(date)
                                .endOf('day')
                                .format()
                            : null,
                        });
                      }}
                      value={
                        localFilter.flaggedOnLte !== undefined &&
                        localFilter.flaggedOnLte !== null
                          ? moment(localFilter.flaggedOnLte)
                          : ''
                      }
                      toToday
                      validLte={localFilter.flaggedOnGte}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
            </SectionDivider>
          </GridItem>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('flagged_ad.filters.section.types')}>
              <GridItem>
                <GridContainer>
                  <GridItem xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          className={classes.checked}
                          onClick={() =>
                            this.handleFlagsCheck(flags.offensive.value)
                          }
                          checked={
                            selectedFlags.indexOf(flags.offensive.value) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                        />
                      }
                      id="flaggedAdOffensiveCheckbox"
                      label={flags.offensive.name}
                      style={{ width: '100%' }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          className={classes.checked}
                          onClick={() =>
                            this.handleFlagsCheck(flags.spam.value)
                          }
                          checked={
                            selectedFlags.indexOf(flags.spam.value) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                        />
                      }
                      id="flaggedAdSpamCheckbox"
                      label={flags.spam.name}
                      style={{ width: '100%' }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          className={classes.checked}
                          onClick={() =>
                            this.handleFlagsCheck(flags.classified.value)
                          }
                          checked={
                            selectedFlags.indexOf(flags.classified.value) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                        />
                      }
                      id="flaggedAdClassifiedCheckbox"
                      label={flags.classified.name}
                      style={{ width: '100%' }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          className={classes.checked}
                          onClick={() =>
                            this.handleFlagsCheck(flags.scam.value)
                          }
                          checked={
                            selectedFlags.indexOf(flags.scam.value) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                        />
                      }
                      id="flaggedAdScamCheckbox"
                      label={flags.scam.name}
                      style={{ width: '100%' }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          className={classes.checked}
                          onClick={() =>
                            this.handleFlagsCheck(flags.joke.value)
                          }
                          checked={
                            selectedFlags.indexOf(flags.joke.value) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                        />
                      }
                      id="flaggedAdJokeCheckbox"
                      label={flags.joke.name}
                      style={{ width: '100%' }}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridContainer justify="flex-end">
                <GridItem>
                  <Button
                    color="primary"
                    simple
                    className={classes.buttons}
                    onClick={() =>
                      this.setState(
                        {
                          selectedFlags:
                            selectedFlags.length > 0
                              ? []
                              : defaultSelectedFlags,
                        },
                        this.updateSelectedCheckboxes,
                      )
                    }
                    id="unselectAll"
                    key="unselectAll"
                    data-cy="flagsUnselectAll"
                  >
                    {selectedFlags.length > 0
                      ? t('ad.filters.status.uncheck')
                      : t('ad.filters.status.check')}
                  </Button>
                </GridItem>
              </GridContainer>
            </SectionDivider>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <Button
              color="primary"
              simple
              className={classes.buttons}
              onClick={this.expandAnimation}
              id="hideFilters"
              key="hideFilters"
            >
              <ArrowUp />
              {t('ad.filters.shrink')}
            </Button>
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <GridContainer justify="flex-end">
              <GridItem>
                <Button
                  color="danger"
                  simple
                  className={classes.buttons}
                  onClick={() => {
                    const defaultDateFilters = {
                      flaggedOnGte: moment()
                        .subtract(5, 'day')
                        .startOf('day')
                        .format(),
                      flaggedOnLte: moment()
                        .endOf('day')
                        .format(),
                    };
                    this.setState({
                      selectedFlags: defaultSelectedFlags,
                      localFilter: { ...defaultDateFilters },
                    });
                    restartFilters();
                  }}
                  id="restartFilters"
                  key="restartFilters"
                >
                  {t('ad.filters.clean')}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  /**
   * @description
   * Render full filter's box.
   * @returns {*} - AdFilter's box.
   */
  render() {
    const { showCollapseFilters } = this.props;
    return (
      <Card>
        <CardBody>
          {!showCollapseFilters
            ? this.renderFilters()
            : this.renderCollapsedFilters()}
        </CardBody>
      </Card>
    );
  }
}

/**
 * FlaggedAdFilter PropTypes
 * @param classes - styles received from withStyles
 * @param appliedFilter - prop to know applied filters
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with string translations
 * @param showCollapseFilters - prop used to show full or collapsed filters
 * @callback filter - callback called when user clicks filter button
 * @callback showFilters - callback called when user collapse filters button
 * @callback restartFilters - callback called when user restart filters button
 */

FlaggedAdFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  showCollapseFilters: PropTypes.bool.isRequired,
  showFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withStyles(FilterStyles)(FlaggedAdFilter));
